import * as React from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete'
import { assetsApi, contractorsApi, inspectionsApi } from '../../api-interface';
import { AnalysisResponse, AssetResponse, Contractor, DocumentAIAnalysis } from '../../api';
import { Typography, Tooltip, Box, useTheme, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, TextField } from '@mui/material';
import UnstyledLink from '../../components/common/UnstyledLink/UnstyledLink';
import ChecklistIcon from '@mui/icons-material/Checklist';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import HelpIcon from '@mui/icons-material/Help';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TenantContext } from '../..';
import { useSnackbar } from 'notistack';
import { GridRowsProp, GridColDef, getGridSingleSelectOperators } from '@mui/x-data-grid';
import StyledDataGrid, { stringFilterOperators } from '../../components/common/StyledDataGrid/StyledDataGrid';
import EditIcon from '@mui/icons-material/Edit';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";


export async function loader(tenant_id, contractor_id) {
  const contractor = await contractorsApi(tenant_id).getContractorContractorsContractorIdGet(contractor_id);
  const analyses = await contractorsApi(tenant_id).getContractorAnalysesContractorsContractorIdAnalysesGet(contractor_id);
  const ret = { "contractor": contractor.data, "analyses":analyses.data };
  return ret;
}


const ContractorInfo = () => {
  const data = useLoaderData() as any;
  const contractor = data["contractor"] as Contractor;
  const analyses = data["analyses"] as AnalysisResponse[];;
  const tenant_id = React.useContext(TenantContext)
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate()

  const inspectionRow = (inspection: AnalysisResponse) => {
    return {
      id: inspection.analysis.id,
      asset_address_1: inspection.asset_add_1 ? String(inspection.asset_add_1) : null,
      inspection_date: inspection.analysis.inspection_date ? new Date(String(inspection.analysis.inspection_date)) : null,
      engineer: inspection.analysis.engineer_name ? String(inspection.analysis.engineer_name) : "",
      next_inspection_due: inspection.analysis.next_inspection_due ? new Date(String(inspection.analysis.next_inspection_due)) : null,
      outcome: inspection.analysis.outcome == "pass" ? "Compliant" : inspection.analysis.outcome == "warning" ? "Warning" : "Needs Attention",
      asset_id: inspection.asset_id,
      analysis_id: inspection.analysis.id
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'asset_address_1', headerName: 'Asset Address Line 1', flex: 1, minWidth: 200, headerClassName: 'table-header', filterOperators: stringFilterOperators, renderCell: (params) => {
        return params.value ? params.value : <HelpIcon sx={{ color: theme.palette.text.disabled, verticalAlign: 'middle' }}></HelpIcon>
      }
    },
    {
      field: 'inspection_date', headerName: 'LGSR Inspection Date', flex: 1, minWidth: 180, type: 'date', headerClassName: 'table-header', renderCell: (params) => {
        return params.value?.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      }
    },
    { field: 'engineer', headerName: 'Engineer', flex: 1, minWidth: 180, headerClassName: 'table-header', filterOperators: stringFilterOperators},
    {
      field: 'next_inspection_due', headerName: 'Next LGSR Inspection Due', headerClassName: 'table-header', flex: 1, minWidth: 180, type: 'date', renderCell: (params) => {
        return params.value ? params.value.toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : <HelpIcon sx={{ color: theme.palette.text.disabled, verticalAlign: 'middle' }}></HelpIcon>
      }
    },
    {
      field: 'outcome', headerName: 'LGSR Outcome', flex: 1, minWidth: 180, headerClassName: 'table-header', type: 'singleSelect', valueOptions: ['Compliant', 'Warning', 'Needs Attention'], filterOperators: getGridSingleSelectOperators(), renderCell: (params) => {
        return params.value === "Compliant" ?
          <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.success.main}><TaskAltIcon fontSize="inherit" /> Compliant</Typography>
          : params.value === "Warning" ? <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.warning.main}><ErrorOutlineIcon fontSize="inherit" /> Warning</Typography>
            : <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.error.main}><ErrorOutlineIcon fontSize="inherit" /> Needs Attention</Typography>
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerClassName: 'table-header',
      flex: 1,
      minWidth: 100,
      getActions: (params) => [
        <UnstyledLink to={"/dashboard/" + tenant_id + "/inspections/" + params.row.analysis_id + "/validations"}><Tooltip title="Verifications"><ChecklistIcon sx={{ verticalAlign: 'middle' }} /></Tooltip></UnstyledLink>,
        <Tooltip title="Delete LGSR Inspection">
          <DeleteIcon onClick={async () => {
            if (window.confirm("Are you sure you want to remove this inspection?")) {
              try {
                await inspectionsApi(tenant_id).deleteAnalysisInspectionsAnalysisIdDelete(params.row.id)
                enqueueSnackbar("LGSR Inspection deleted", { variant: "success" })
              } catch (e) {
                enqueueSnackbar("Error deleting inspection: " + e, { variant: "error" })
              }

            }
            navigate("")
          }} sx={{ ml: 1, verticalAlign: 'middle', cursor: 'pointer' }} />
        </Tooltip>
      ],
    }
  ];
  const rows: GridRowsProp = analyses.map(inspectionRow);


  const theme = useTheme();
  const [editOpen, setEditOpen] = React.useState(false);
  const [apiError, setApiError] = React.useState<string>("");

  const schema = yup
    .object({
      name: yup.string().required(),
      notes: yup.string(),
    })
    .required()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: contractor.name,
      notes: String(contractor.notes) || '',
    }
  });

  const handleEditOpen = () => {
    reset({
      name: contractor.name,
      notes: String(contractor.notes) || '',
    });
    setEditOpen(true);
    setApiError("");
  };
  const handleEditClose = () => setEditOpen(false);

  const hadleEditContractor = async (x) => {
    try {
      await contractorsApi(tenant_id).updateContractorContractorsContractorIdPatch(
        contractor.id,
        {
          "name": x.name,
          "notes": x.notes,
        }
      )
      enqueueSnackbar('Asset address updated', { variant: "success" });
      setEditOpen(false);
      window.location.reload(); // reload to show updated address
    } catch (e) {
      if (e?.response?.data?.detail) {
        setApiError(e.response.data.detail);
      } else {
        enqueueSnackbar("Error: " + e, { variant: "error" });
      }
    }
  };


  return (
    <Box display={"flex"} flexDirection={"column"} gap={5}>
      <Box display="flex" flexDirection={{sm: "row", xs: "column"}} alignItems="center" justifyContent="space-between">
        <Typography variant="h4">Contractor Overview - {contractor.name}</Typography>
        <Button startIcon={<EditIcon />} variant="outlined" onClick={handleEditOpen} sx={{ alignSelf: 'flex-start' }}>Edit Contractor</Button>
      </Box>
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Contractor</DialogTitle>
        <form onSubmit={handleSubmit(hadleEditContractor)}>
          <DialogContent>
            <FormControl fullWidth>
              <TextField
                margin="normal"
                label="Name"
                placeholder="Name"
                {...register('name')}
                error={!!errors.name}
                helperText={errors.name?.message?.toString()}
                type="text"
                variant='outlined'
                required
              />
              <TextField
                margin="normal"
                label="Notes"
                placeholder="Notes"
                {...register('notes')}
                multiline
                rows={2}
                error={!!errors.notes}
                helperText={errors.notes?.message?.toString()}
                type="text"
                variant='outlined'
              />
              <Typography mt={2} color={theme.palette.error.main} display={apiError ? "block" : "none"}>{apiError}</Typography>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditClose} variant="outlined">Cancel</Button>
            <Button type="submit" variant="contained">Save</Button>
          </DialogActions>
        </form>
      </Dialog>
      <Box display={"flex"} flexDirection={"row"} gap={2}>
        <Box flexGrow={1}>
          {contractor.notes && <Typography variant="h6" fontWeight={"bold"}>Notes:</Typography>}

          {String(contractor.notes)
            .split('\n')
            .map((line, idx) => (
              <React.Fragment key={idx}>
                {line}
                {idx < String(contractor.notes).split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
        </Box>
      </Box>
      <Box sx={{
        width: '100%'
      }} >
        <Typography variant="h5">LGSR Inspections</Typography>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <StyledDataGrid rows={rows} columns={columns} showToolbar />
        </div>
      </Box>
    </Box>
  )
}

export default ContractorInfo